import axios from 'axios';
import { authService } from './auth.service';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：添加认证 token
api.interceptors.request.use((config) => {
  const token = authService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截器：处理 401 错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      authService.logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface CreateTaskRequest {
  prompt: string;
  style?: string;
  page_count?: number;
  template_id?: string;
  conversion_mode?: 'semantic' | 'fidelity';
  include_cover_and_end?: boolean;
}

export interface CreateTaskResponse {
  taskId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
}

export interface TaskStatusResponse {
  taskId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress: number;
  result?: {
    filePath: string;
    fileSize: number;
    fileName: string;
  };
  error?: string;
  createdAt: string;
  completedAt?: string;
}

export interface Template {
  id: string;
  name: string;
  description: string;
  category?: string;
  colors?: {
    primary: string;
    accent: string;
    background: string;
  };
}

export interface AdminTask {
  id: string;
  prompt: string;
  page_count?: number;
  style?: string;
  template_id?: string;
  conversion_mode?: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress: number;
  error?: string;
  result_file_path?: string;
  result_file_size?: number;
  result_file_name?: string;
  created_at: string;
  completed_at?: string;
  updated_at: string;
  duration_time?: string;
}

export interface AdminTaskListResponse {
  tasks: AdminTask[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminTaskStats {
  total: number;
  queued: number;
  processing: number;
  completed: number;
  failed: number;
}

export interface AdminTaskDetail extends AdminTask {
  system_prompt?: string;
  user_prompt?: string;
  generated_html?: string;
}

export const apiService = {
  async createTask(
    prompt: string,
    style?: string,
    templateId?: string,
    conversionMode: 'semantic' | 'fidelity' = 'fidelity',
    includeCoverAndEnd?: boolean,
    pageCount?: number
  ): Promise<CreateTaskResponse> {
    const response = await api.post<CreateTaskResponse>('/tasks/create', {
      prompt,
      style,
      template_id: templateId,
      conversion_mode: conversionMode,
      include_cover_and_end: includeCoverAndEnd,
      page_count: pageCount,
    });
    return response.data;
  },

  async getTaskStatus(taskId: string): Promise<TaskStatusResponse> {
    const response = await api.get<TaskStatusResponse>(`/tasks/${taskId}/status`);
    return response.data;
  },

  getDownloadUrl(taskId: string): string {
    return `/api/tasks/${taskId}/download`;
  },

  async getTemplates(): Promise<Template[]> {
    const response = await api.get<Template[]>('/templates');
    return response.data;
  },

  async getTemplate(id: string): Promise<Template> {
    const response = await api.get<Template>(`/templates/${id}`);
    return response.data;
  },

  // 管理员 API
  async getAdminTasks(params: {
    page?: number;
    pageSize?: number;
    status?: string;
    keyword?: string;
  }): Promise<AdminTaskListResponse> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
    if (params.status) searchParams.set('status', params.status);
    if (params.keyword) searchParams.set('keyword', params.keyword);
    const response = await api.get<AdminTaskListResponse>(`/admin/tasks?${searchParams.toString()}`);
    return response.data;
  },

  async getAdminTaskStats(): Promise<AdminTaskStats> {
    const response = await api.get<AdminTaskStats>('/admin/tasks/stats');
    return response.data;
  },

  async getAdminTaskDetail(id: string): Promise<AdminTaskDetail> {
    const response = await api.get<AdminTaskDetail>(`/admin/tasks/${id}`);
    return response.data;
  },
};
